import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { harareInputToIso } from '../../utils/time';
import { brand, text, bg } from '../../theme/colors';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Button,
    Avatar,
    Chip,
    Alert,
    AlertTitle,
    Paper,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Snackbar,
    Collapse
} from '@mui/material';
import {
    Home,
    AccountBalance,
    Warning,
    Schedule,
    Message,
    VideoCall,
    CheckCircle,
    Info,
    ArrowForward,
    Map,
    Add,
    ExpandMore,
    ExpandLess,
    Gavel,
    HomeWork,
    VerifiedUser
} from '@mui/icons-material';
import VerifierLocationTracker from '../VerifierLocationTracker';
import UnifiedProjectWizard from '../projects/UnifiedProjectWizard';
import PendingVerificationBanner from '../PendingVerificationBanner';
import DashboardStatsCards from '../dashboard/DashboardStatsCards';
import DialogCloseButton from '../common/DialogCloseButton';
import ImprovedProjectCard from '../projects/ImprovedProjectCard';
import ActivityFeed from '../dashboard/ActivityFeed';
import SmartDashboardInsights from '../dashboard/SmartDashboardAssistant';
import PendingReviews from '../projects/PendingReviews';
import ClientGettingStartedChecklist from '../dashboard/ClientGettingStartedChecklist';
import SavedHomesCard from '../dashboard/SavedHomesCard';

/**
 * Enhanced Client Dashboard - Diaspora-First Design
 * Priority: Trust, Transparency, Remote Control
 */
const EnhancedClientDashboard = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    
    // State management
    const [dashboardData, setDashboardData] = useState({
        activeProjects: [],
        allProjects: [], // Add state for all projects
        escrowAccounts: [],
        recentActivity: [],
        insights: [],
        alerts: [],
        actionItems: {},
        stats: {
            totalProjects: 0,
            activeEscrows: 0,
            totalInvested: 0,
            completedProjects: 0,
            fundsInEscrow: 0,
            fundsReleased: 0
        }
    });
    
    const [clientProfile, setClientProfile] = useState({
        diasporaLocation: '',
        timezone: 'Africa/Harare',
        currency: 'USD',
        language: 'en',
        localTime: new Date(),
        zimbabweTime: new Date()
    });
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedTab, setSelectedTab] = useState(0);
    const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);
    const [showScheduleDialog, setShowScheduleDialog] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [expandedActions, setExpandedActions] = useState({ awaitingReview: true, needFunding: true, needContractor: true, openForBids: false });

    const [messages, setMessages] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
    const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
    const [scheduleForm, setScheduleForm] = useState({
        projectId: '',
        visitType: 'video_call',
        scheduledDate: '',
        notes: ''
    });
    const [activeVerifications, setActiveVerifications] = useState([]);

    // Mark welcome as seen on first dashboard visit (enables Smart Insights for next login)
    useEffect(() => {
        if (user?.id && !user?.profile?.welcomeSeen) {
            api.put('/auth/users/profile', { welcome_seen: true }).catch(() => {});
        }
    }, [user?.id]);

    // Timezone and locale management
    useEffect(() => {
        const updateTimes = () => {
            const now = new Date();
            const zimbabweTime = new Intl.DateTimeFormat('en-ZW', {
                timeZone: 'Africa/Harare',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            }).format(now);
            
            setClientProfile(prev => ({
                ...prev,
                localTime: now,
                zimbabweTime
            }));
        };
        
        updateTimes();
        const interval = setInterval(updateTimes, 60000); // Update every minute
        
        return () => clearInterval(interval);
    }, []);

    // Load messages
    const loadMessages = useCallback(async () => {
        try {
            // /api/messaging/conversations references non-existent schema columns
            // (conversations.client_id etc). Use the correct participant-based API instead.
            const response = await api.get('/conversations');

            if (response.data.success) {
                // conversations.js returns { data: { conversations: [...] } }
                const conversations = response.data.data?.conversations || [];
                setMessages(conversations);
                setUnreadMessagesCount(conversations.filter(c => c.unread_count > 0).length);
            }
        } catch (err) {
            console.error('Failed to load messages:', err);
        }
    }, []);

    // Load notifications
    const loadNotifications = useCallback(async () => {
        try {
            const response = await api.get('/notifications?limit=20');

            if (response.data.success) {
                setNotifications(response.data.data || []);
                setUnreadNotificationsCount(response.data.unreadCount || 0);
            }
        } catch (err) {
            console.error('Failed to load notifications:', err);
        }
    }, []);

    // Load active verifications
    const loadActiveVerifications = useCallback(async () => {
        try {
            const response = await api.get(`/verification-requests?clientId=${user?.id}&status=in_progress,accepted`);

            if (response.data.success) {
                setActiveVerifications(response.data.data || []);
            }
        } catch (err) {
            console.error('Failed to load active verifications:', err);
        }
    }, [user?.id]);

    // Load dashboard data
    const loadDashboardData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            if (!user?.id) {
                console.log('No user ID available');
                setLoading(false);
                return;
            }

            // Fetch dashboard data
            const dashboardResponse = await api.get(`/dashboard/${user.id}`);

            // Fetch client profile
            const profileResponse = await api.get(`/dashboard/${user.id}/profile`);

            // Fetch all projects for Smart Insights
            const projectsResponse = await api.get('/projects')
                .catch(() => ({ data: { data: { projects: [] } } }));

            // Load messages, notifications, and active verifications
            await Promise.all([loadMessages(), loadNotifications(), loadActiveVerifications()]);

            if (dashboardResponse.data.success) {
                const data = dashboardResponse.data.data;
                
                // Update dashboard data
                setDashboardData({
                    activeProjects: data.activeProjects || [],
                    allProjects: projectsResponse.data.data?.projects || [], // Add all projects for Smart Insights
                    escrowAccounts: data.escrowAccounts || [],
                    recentActivity: data.recentActivity || [],
                    insights: [],
                    alerts: data.alerts || [],
                    actionItems: data.actionItems || {},
                    stats: data.stats || {
                        totalProjects: 0,
                        activeEscrows: 0,
                        totalInvested: 0,
                        completedProjects: 0,
                        fundsInEscrow: 0,
                        fundsReleased: 0
                    }
                });
            }

            // Update client profile
            if (profileResponse.data.success) {
                const profileData = profileResponse.data.data;
                setClientProfile(prev => ({
                    ...prev,
                    diasporaLocation: profileData.diasporaLocation || prev.diasporaLocation,
                    timezone: profileData.timezone || prev.timezone,
                    currency: profileData.currency || prev.currency,
                    language: profileData.language || prev.language
                }));
            }
            
            // Check if first-time user
            if (!localStorage.getItem('welcome_shown') && dashboardResponse.data.data.activeProjects.length === 0) {
                setShowWelcomeDialog(true);
                localStorage.setItem('welcome_shown', 'true');
            }

        } catch (err) {
            setError(err.message || 'Failed to load dashboard data');
            console.error('Failed to load dashboard data:', err);
            
            // Set empty data on error to avoid undefined errors
            setDashboardData({
                activeProjects: [],
                allProjects: [],
                escrowAccounts: [],
                recentActivity: [],
                insights: [],
                alerts: [],
                actionItems: {},
                stats: {
                    totalProjects: 0,
                    activeEscrows: 0,
                    totalInvested: 0,
                    completedProjects: 0,
                    fundsInEscrow: 0,
                    fundsReleased: 0
                }
            });
        } finally {
            setLoading(false);
        }
    }, [user, loadMessages, loadNotifications]);

    useEffect(() => {
        loadDashboardData();
    }, [loadDashboardData]);

    // Format currency with user preference
    const formatCurrency = (amount, currency = clientProfile.currency) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency
        }).format(amount);
    };

    // Navigation handlers
    const [useNewWizard, setUseNewWizard] = useState(false);
    
    const handleCreateProject = () => {
        // Use new wizard - keeping old dialog available until new one is perfected
        setUseNewWizard(true);
    };
    
    const handleViewAllProjects = () => navigate('/projects');
    const handleEditProject = (project) => navigate(`/projects?edit=${project.id}`);
    const handleProjectPayment = (project) => navigate(`/payments?project=${project.id}`);
    
    // Schedule Site Visit handler
    const handleScheduleSiteVisit = async () => {
        try {
            const response = await api.post('/site-visits', {
                ...scheduleForm,
                // Picker is Harare (CAT) wall-clock; anchor to CAT before sending.
                scheduledDate: harareInputToIso(scheduleForm.scheduledDate) || scheduleForm.scheduledDate,
                clientUserId: user.id
            });

            if (response.data.success) {
                setSnackbar({ open: true, message: 'Site visit scheduled successfully!', severity: 'success' });
                setShowScheduleDialog(false);
                setScheduleForm({
                    projectId: '',
                    visitType: 'video_call',
                    scheduledDate: '',
                    notes: ''
                });
                loadDashboardData(); // Refresh dashboard
            }
        } catch (err) {
            console.error('Failed to schedule site visit:', err);
            setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to schedule site visit', severity: 'error' });
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress size={60} />
            </Box>
        );
    }

    const recentProjects = [...dashboardData.activeProjects]
        .sort((a, b) => {
            const aDate = a.lastUpdate || a.createdAt || 0;
            const bDate = b.lastUpdate || b.createdAt || 0;
            return new Date(bDate) - new Date(aDate);
        })
        .slice(0, 2);

    const getDashboardProjectInsight = (project) => {
        const progress = parseFloat(project.progress || project.completion_percentage || 0);
        const hasContractor = !!(project.contractor?.name || project.contractor_name || project.manual_contractor_name);
        const budget = parseFloat(project.budget || 0);

        if (progress < 25 && project.status === 'in_progress') {
            return 'Project is in early stages. Consider setting up regular check-ins with the contractor.';
        }
        if (progress >= 25 && progress < 50) {
            return 'Project is progressing steadily. Monitor milestone completion to stay on track.';
        }
        if (progress >= 50 && progress < 75) {
            return 'Project is over halfway complete. Review budget utilization and timeline.';
        }
        if (progress >= 75 && progress < 100) {
            return 'Project is nearing completion. Prepare for final inspections and closeout.';
        }
        if (!hasContractor && project.status === 'open_for_bids') {
            return 'Actively seeking contractors. Consider reviewing and responding to proposals.';
        }
        if (budget === 0) {
            return 'Budget not set. Add project budget to enable better financial tracking.';
        }
        return null;
    };

    const toProjectCardModel = (project) => ({
        ...project,
        title: project.title || project.name || 'Untitled Project',
        progress: project.progress ?? project.completion_percentage ?? 0,
        contractor: project.contractor || (
            project.contractor_name || project.manual_contractor_name
                ? { name: project.contractor_name || project.manual_contractor_name }
                : null
        ),
        aiInsight: project.aiInsight || getDashboardProjectInsight(project)
    });

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: bg.page, minHeight: '100vh' }}>
            {/* Shown to clients under review (e.g. recent-signup cohort): they can
                browse but booking/quotes/payments are gated until approved. */}
            <PendingVerificationBanner
                approvalStatus={user?.approvalStatus || user?.profile?.approvalStatus || 'approved'}
                role="client"
            />
            {/* Dashboard Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: text.primary }}>
                    Dashboard
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {/* Two distinct verification paths: property checks (fixed-price
                        visit to a property you own) vs verifying project/milestone
                        work (verifier marketplace). Previously one ambiguous button. */}
                    <Button
                        variant="outlined"
                        startIcon={<HomeWork />}
                        onClick={() => navigate('/property-check/book')}
                        sx={{
                            borderColor: brand.secondary,
                            color: brand.secondary,
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                            py: 1.5,
                            '&:hover': { backgroundColor: brand.secondary, color: 'white' }
                        }}
                    >
                        Book a Property Check
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<VerifiedUser />}
                        onClick={() => navigate('/verification-requests/create')}
                        sx={{
                            borderColor: brand.primary,
                            color: brand.primary,
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                            py: 1.5,
                            '&:hover': { backgroundColor: brand.primary, color: 'white' }
                        }}
                    >
                        Verify Project Work
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleCreateProject}
                        sx={{
                            bgcolor: brand.primary,
                            '&:hover': { bgcolor: brand.primaryDark },
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 3,
                            py: 1.5
                        }}
                    >
                        New Project
                    </Button>
                </Box>
            </Box>

            {/* Welcome Dialog for First-Time Users — surfaces the two paths */}
            <Dialog open={showWelcomeDialog} onClose={() => setShowWelcomeDialog(false)} maxWidth="md" fullWidth>
                <DialogCloseButton onClick={() => setShowWelcomeDialog(false)} />
                <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
                    <Typography variant="overline" sx={{ color: brand.primary, fontWeight: 700, letterSpacing: '0.1em' }}>
                        WELCOME TO EQAYA
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em', mt: 0.5 }}>
                        What would you like to do?
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 1 }}>
                        Two ways to use Eqaya — pick whichever matches what you need.
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} md={6}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    height: '100%',
                                    border: `2px solid ${brand.primary}`,
                                    borderRadius: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <Avatar sx={{ bgcolor: brand.primary, mb: 2, width: 48, height: 48 }}>
                                    <Home />
                                </Avatar>
                                <Typography variant="overline" sx={{ color: brand.primary, fontWeight: 700, letterSpacing: '0.06em' }}>
                                    BUILD / RENOVATE
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, lineHeight: 1.2 }}>
                                    Start a construction project
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, flex: 1 }}>
                                    Hire vetted contractors, fund work in escrow, and have verifiers
                                    document every milestone before payment moves.
                                </Typography>
                                <Button
                                    variant="contained"
                                    endIcon={<ArrowForward />}
                                    onClick={() => {
                                        setShowWelcomeDialog(false);
                                        handleCreateProject();
                                    }}
                                    sx={{
                                        bgcolor: brand.primary,
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        '&:hover': { bgcolor: brand.primaryDark },
                                    }}
                                >
                                    Start a project
                                </Button>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    height: '100%',
                                    border: `2px solid ${brand.secondary}`,
                                    borderRadius: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <Avatar sx={{ bgcolor: brand.secondary, mb: 2, width: 48, height: 48 }}>
                                    <HomeWork />
                                </Avatar>
                                <Typography variant="overline" sx={{ color: brand.secondary, fontWeight: 700, letterSpacing: '0.06em' }}>
                                    ALREADY OWN A PROPERTY
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, lineHeight: 1.2 }}>
                                    Property Check
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, flex: 1 }}>
                                    Send a verified person to your Zimbabwe property and get a
                                    photo-backed condition report within 48 hours.
                                </Typography>
                                <Button
                                    variant="contained"
                                    endIcon={<ArrowForward />}
                                    onClick={() => {
                                        setShowWelcomeDialog(false);
                                        navigate('/property-check/book');
                                    }}
                                    sx={{
                                        bgcolor: brand.secondary,
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        '&:hover': { bgcolor: brand.secondaryDark },
                                    }}
                                >
                                    Book a Property Check
                                </Button>
                            </Paper>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3, justifyContent: 'center' }}>
                    <Button onClick={() => setShowWelcomeDialog(false)} sx={{ textTransform: 'none' }}>
                        I'll explore first
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Unified Project Creation Wizard */}
            <UnifiedProjectWizard
                open={useNewWizard}
                onClose={() => {
                    setUseNewWizard(false);
                }}
                onComplete={(projectId) => {
                    setUseNewWizard(false);
                    loadDashboardData();
                    if (projectId) {
                        navigate(`/projects/${projectId}`);
                    }
                }}
            />

            {/* Alerts Section */}
            {dashboardData.alerts.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    {dashboardData.alerts.map(alert => (
                        <Alert 
                            key={alert.id}
                            severity={alert.severity}
                            sx={{ mb: 1 }}
                            action={
                                alert.actionable && (
                                    <Button color="inherit" size="small">
                                        {alert.action}
                                    </Button>
                                )
                            }
                        >
                            <AlertTitle>{alert.title}</AlertTitle>
                            {alert.message}
                        </Alert>
                    ))}
                </Box>
            )}

            {/* Smart Insights — hidden on first login */}
            {user?.profile?.welcomeSeen && <SmartDashboardInsights />}

            {/* Stats Cards */}
            <DashboardStatsCards
                stats={dashboardData.stats}
                projects={dashboardData.activeProjects}
                onFilterChange={(filter) => {
                    // Navigate to projects page with filter
                    if (filter.status === 'all') {
                        navigate('/projects');
                    } else {
                        navigate(`/projects?status=${filter.status}`);
                    }
                }}
            />

            {/* Getting Started Checklist — shown until all steps are complete */}
            {!loading && (
                <ClientGettingStartedChecklist
                    profileComplete={!!(user?.profile?.firstName && user?.profile?.lastName)}
                    hasProject={dashboardData.stats.totalProjects > 0}
                    hasEscrow={dashboardData.stats.fundsInEscrow > 0}
                    hasContractor={dashboardData.activeProjects.some(p => p.contractor_id || p.contractor)}
                    onCreateProject={handleCreateProject}
                />
            )}

            {/* ── PROJECT ACTION CENTER ── */}
            {!loading && (() => {
                const ai = dashboardData.actionItems || {};
                const awaitingReview = ai.awaitingReview || [];
                const needFunding = ai.needFunding || [];
                const needContractor = ai.needContractor || [];
                const inProgress = ai.inProgress || [];
                const changeOrders = ai.changeOrders || [];
                // "Waiting for contractor bids" (open_for_bids) is intentionally
                // EXCLUDED here: a posted project waiting for bids is a passive
                // state with no client action to take, so it must not trigger the
                // red "Action Required" alarm. It is surfaced separately below as
                // a calm, informational card.
                const totalActions = awaitingReview.length + needFunding.length + needContractor.length + changeOrders.length;

                if (totalActions === 0) return null;

                const formatTimeRemaining = (hours) => {
                    if (hours <= 0) return 'Auto-releasing...';
                    if (hours < 1) return `${Math.round(hours * 60)}m left`;
                    if (hours < 24) return `${Math.round(hours)}h left`;
                    const days = Math.floor(hours / 24);
                    const rh = Math.round(hours % 24);
                    return `${days}d ${rh}h left`;
                };

                const MilestoneReviewRow = ({ milestone }) => {
                    const hoursLeft = milestone.hoursRemaining;
                    const isUrgent = hoursLeft <= 6;
                    const isCritical = hoursLeft <= 1;
                    return (
                        <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 1.5, py: 1.2, px: 2,
                            borderBottom: '1px solid #f0f0f0',
                            bgcolor: isCritical ? '#fff3f3' : 'transparent',
                            '&:last-child': { borderBottom: 'none' },
                            '&:hover': { bgcolor: '#fafafa' },
                            flexWrap: { xs: 'wrap', sm: 'nowrap' }
                        }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {milestone.projectTitle}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, mt: 0.3 }}>
                                    <Typography variant="caption" sx={{ color: '#666' }}>
                                        Milestone {milestone.milestoneNumber} &mdash; {formatCurrency(milestone.amount)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: isUrgent ? '#d32f2f' : '#e65100', fontWeight: isUrgent ? 700 : 600 }}>
                                        {formatTimeRemaining(hoursLeft)}
                                    </Typography>
                                </Box>
                            </Box>
                            {!milestone.verifierHired && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => navigate(`/projects/${milestone.projectId}?action=verify&milestone=${milestone.id}`)}
                                    sx={{
                                        textTransform: 'none', fontWeight: 600, fontSize: '0.7rem',
                                        borderColor: '#7B1FA2', color: '#7B1FA2',
                                        whiteSpace: 'nowrap', flexShrink: 0,
                                    }}
                                >
                                    Hire Verifier
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                size="small"
                                onClick={() => navigate(`/projects/${milestone.projectId}`)}
                                sx={{
                                    bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' },
                                    textTransform: 'none', fontWeight: 700, fontSize: '0.75rem',
                                    whiteSpace: 'nowrap', flexShrink: 0, px: 2
                                }}
                            >
                                Review Now
                            </Button>
                        </Box>
                    );
                };

                const toggleSection = (key) => setExpandedActions(prev => ({ ...prev, [key]: !prev[key] }));

                const ActionRow = ({ project, actionLabel, actionColor, onClick, urgencyText }) => (
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5, py: 1.2, px: 2,
                        borderBottom: '1px solid #f0f0f0',
                        '&:last-child': { borderBottom: 'none' },
                        '&:hover': { bgcolor: '#fafafa' },
                        flexWrap: { xs: 'wrap', sm: 'nowrap' }
                    }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {project.title}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, mt: 0.3 }}>
                                <Typography variant="caption" sx={{ color: '#666' }}>
                                    {formatCurrency(project.budget)}
                                </Typography>
                                {urgencyText && (
                                    <Typography variant="caption" sx={{ color: project.daysWaiting > 30 ? '#e53935' : '#999' }}>
                                        {urgencyText}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={onClick}
                            sx={{
                                bgcolor: actionColor,
                                '&:hover': { bgcolor: actionColor, filter: 'brightness(0.9)' },
                                textTransform: 'none', fontWeight: 700, fontSize: '0.75rem',
                                whiteSpace: 'nowrap', flexShrink: 0, px: 2
                            }}
                        >
                            {actionLabel}
                        </Button>
                    </Box>
                );

                const SectionHeader = ({ icon, label, count, color, bgColor, sectionKey }) => (
                    <Box
                        onClick={() => toggleSection(sectionKey)}
                        sx={{
                            display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, px: 2,
                            bgcolor: bgColor, cursor: 'pointer', userSelect: 'none',
                            borderBottom: expandedActions[sectionKey] ? `2px solid ${color}` : 'none',
                            '&:hover': { filter: 'brightness(0.97)' }
                        }}
                    >
                        {icon}
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color, flex: 1 }}>
                            {label}
                        </Typography>
                        <Chip label={count} size="small" sx={{ bgcolor: color, color: '#fff', fontWeight: 700, height: 22, fontSize: '0.75rem' }} />
                        {expandedActions[sectionKey] ? <ExpandLess sx={{ color }} /> : <ExpandMore sx={{ color }} />}
                    </Box>
                );

                return (
                    <Card sx={{ mb: 3, border: '2px solid #e53935', borderRadius: 2, overflow: 'hidden' }}>
                        {/* Header */}
                        <Box sx={{ bgcolor: '#d32f2f', px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Warning sx={{ color: '#fff', fontSize: 24 }} />
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 800, lineHeight: 1.2 }}>
                                    Action Required
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                                    {totalActions} project{totalActions !== 1 ? 's' : ''} need{totalActions === 1 ? 's' : ''} your attention to move forward
                                </Typography>
                            </Box>
                        </Box>

                        {/* Change Orders needing attention — high urgency */}
                        {changeOrders.length > 0 && (
                            <Box sx={{ p: 2, borderBottom: '1px solid #f0f0f0' }}>
                                <Alert severity="warning" sx={{ mb: 0 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                        {changeOrders.length} change order{changeOrders.length > 1 ? 's' : ''} need your attention
                                    </Typography>
                                    {changeOrders.slice(0, 3).map((co) => (
                                        <Box key={co.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                                            <Typography variant="body2">
                                                "{co.title}" on {co.project_title} — {co.action === 'fund' ? 'approved, needs funding' : 'awaiting your decision'}
                                            </Typography>
                                            <Button size="small" onClick={() => navigate(`/projects/${co.project_id}`)}>Open</Button>
                                        </Box>
                                    ))}
                                </Alert>
                            </Box>
                        )}

                        {/* Priority 0: Milestones Awaiting Review — ticking timer */}
                        {awaitingReview.length > 0 && (
                            <>
                                <SectionHeader
                                    icon={<Schedule sx={{ color: '#b71c1c', fontSize: 20 }} />}
                                    label="Milestones awaiting your review"
                                    count={awaitingReview.length}
                                    color="#b71c1c"
                                    bgColor="#ffebee"
                                    sectionKey="awaitingReview"
                                />
                                <Collapse in={expandedActions.awaitingReview}>
                                    {awaitingReview.map(m => (
                                        <MilestoneReviewRow key={m.id} milestone={m} />
                                    ))}
                                </Collapse>
                            </>
                        )}

                        {/* Priority 1: Fund Wallet — revenue blocked */}
                        {needFunding.length > 0 && (
                            <>
                                <SectionHeader
                                    icon={<AccountBalance sx={{ color: '#c62828', fontSize: 20 }} />}
                                    label={`Fund wallet to start construction`}
                                    count={needFunding.length}
                                    color="#c62828"
                                    bgColor="#fdecea"
                                    sectionKey="needFunding"
                                />
                                <Collapse in={expandedActions.needFunding}>
                                    {needFunding.map(p => (
                                        <ActionRow
                                            key={p.id}
                                            project={p}
                                            actionLabel="Fund Wallet"
                                            actionColor="#e53935"
                                            onClick={() => navigate(`/projects/${p.id}?action=fund`)}
                                            urgencyText={p.daysWaiting > 0 ? `Waiting ${p.daysWaiting}d` : 'Just assigned'}
                                        />
                                    ))}
                                </Collapse>
                            </>
                        )}

                        {/* Priority 2: Hire Contractor */}
                        {needContractor.length > 0 && (
                            <>
                                <SectionHeader
                                    icon={<Warning sx={{ color: '#e65100', fontSize: 20 }} />}
                                    label={`Hire a contractor to get started`}
                                    count={needContractor.length}
                                    color="#e65100"
                                    bgColor="#fff8e1"
                                    sectionKey="needContractor"
                                />
                                <Collapse in={expandedActions.needContractor}>
                                    {needContractor.map(p => (
                                        <ActionRow
                                            key={p.id}
                                            project={p}
                                            actionLabel="Find Contractor"
                                            actionColor="#f39c12"
                                            onClick={() => navigate(`/projects/${p.id}`)}
                                            urgencyText={p.daysWaiting > 0 ? `Created ${p.daysWaiting}d ago` : 'New'}
                                        />
                                    ))}
                                </Collapse>
                            </>
                        )}

                        {/* Priority 4: In Progress — positive reinforcement */}
                        {inProgress.length > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, px: 2, bgcolor: '#e8f5e9' }}>
                                <CheckCircle sx={{ color: '#2e7d32', fontSize: 20 }} />
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2e7d32', flex: 1 }}>
                                    {inProgress.length} project{inProgress.length !== 1 ? 's' : ''} in progress
                                </Typography>
                                <Chip label="Active" size="small" sx={{ bgcolor: '#2e7d32', color: '#fff', fontWeight: 700, height: 22, fontSize: '0.75rem' }} />
                            </Box>
                        )}
                    </Card>
                );
            })()}

            {/* ── WAITING FOR BIDS ── Calm, informational. A posted project waiting
                for contractor bids needs NO client action, so it is shown here as
                neutral status — never under the red "Action Required" banner. */}
            {!loading && (() => {
                const openForBids = dashboardData.actionItems?.openForBids || [];
                if (openForBids.length === 0) return null;
                return (
                    <Card sx={{ mb: 3, border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
                        <Box sx={{ bgcolor: '#e8eaf6', px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Gavel sx={{ color: '#3949ab', fontSize: 22 }} />
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle1" sx={{ color: '#283593', fontWeight: 700, lineHeight: 1.2 }}>
                                    Live on the marketplace
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#3949ab' }}>
                                    {openForBids.length} project{openForBids.length !== 1 ? 's' : ''} waiting for contractor quotations — nothing needed from you yet
                                </Typography>
                            </Box>
                        </Box>
                        {openForBids.slice(0, 5).map(p => (
                            <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.2, borderBottom: '1px solid #f0f0f0' }}>
                                <Box sx={{ minWidth: 0, mr: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {p.title || p.name || 'Untitled Project'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {p.daysWaiting > 0 ? `Posted ${p.daysWaiting}d ago` : 'Just posted'}
                                    </Typography>
                                </Box>
                                <Button size="small" onClick={() => navigate(`/projects/${p.id}`)} sx={{ textTransform: 'none', flexShrink: 0 }}>
                                    View
                                </Button>
                            </Box>
                        ))}
                        {openForBids.length > 5 && (
                            <Box sx={{ px: 2, py: 1.2, textAlign: 'center' }}>
                                <Button size="small" endIcon={<ArrowForward />} onClick={() => navigate('/projects?status=open_for_bids')} sx={{ textTransform: 'none', fontWeight: 600 }}>
                                    View all {openForBids.length}
                                </Button>
                            </Box>
                        )}
                    </Card>
                );
            })()}

            {/* Main Content Area */}
            <Grid container spacing={3}>
                {/* Left Column - Projects */}
                <Grid item xs={12} lg={8}>
                    {/* Live Verification Tracking - Show when verifications are in progress */}
                    {activeVerifications.length > 0 && (
                        <Card elevation={2} sx={{ mb: 3, bgcolor: 'white', border: '2px solid #e1e8ed', borderLeft: `4px solid ${brand.primary}`, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                    <Map sx={{ color: brand.primary }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Live Verification Tracking
                                    </Typography>
                                    <Chip 
                                        label={`${activeVerifications.length} Active`} 
                                        sx={{ bgcolor: brand.primary, color: 'white' }}
                                        size="small"
                                    />
                                </Box>

                                {activeVerifications.map(verification => (
                                    <Box key={verification.id} sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                                            {verification.requestDetails?.title || 'Verification'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                                            Project: {verification.projectId} • Status: {verification.status}
                                        </Typography>
                                        
                                        {verification.location && (
                                            <VerifierLocationTracker
                                                verificationRequestId={verification.id}
                                                verifierInfo={{
                                                    name: verification.verifier?.name || 'Verifier',
                                                    phone: verification.verifier?.phone
                                                }}
                                                projectLocation={{
                                                    lat: verification.location.coordinates?.lat || verification.location.lat,
                                                    lng: verification.location.coordinates?.lng || verification.location.lng
                                                }}
                                            />
                                        )}
                                    </Box>
                                ))}
                            </CardContent>
                        </Card>
                    )}

                    {/* Your Projects Section */}
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                🏗️ Recent Projects
                            </Typography>
                        </Box>

                        {dashboardData.activeProjects.length === 0 ? (
                                <Paper 
                                    sx={{ 
                                        p: 6, 
                                        textAlign: 'center', 
                                        bgcolor: bg.subtle,
                                        border: '2px dashed #dee2e6'
                                    }}
                                >
                                    <Home sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                                    <Typography variant="h6" gutterBottom>
                                        No Projects Yet
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                        Start your first construction project today
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={handleCreateProject}
                                        startIcon={<ArrowForward />}
                                        sx={{ bgcolor: brand.primary, '&:hover': { bgcolor: brand.primaryDark } }}
                                    >
                                        Create Your First Project
                                    </Button>
                                </Paper>
                            ) : (
                                <Grid container spacing={2}>
                                {recentProjects.map(project => (
                                    <Grid item xs={12} md={6} key={project.id}>
                                        <ImprovedProjectCard
                                            project={toProjectCardModel(project)}
                                            onEdit={handleEditProject}
                                            onPayment={handleProjectPayment}
                                        />
                                    </Grid>
                                ))}
                                </Grid>
                            )}
                            
                            {/* View All Projects Button */}
                            {dashboardData.activeProjects.length > 0 && (
                                <Box sx={{ textAlign: 'center', mt: 3 }}>
                                    <Button
                                        variant="outlined"
                                        onClick={handleViewAllProjects}
                                        endIcon={<ArrowForward />}
                                        sx={{ px: 4, borderColor: brand.primary, color: brand.primary, '&:hover': { backgroundColor: brand.primary, color: 'white' } }}
                                    >
                                        View All My Projects ({dashboardData.stats?.totalProjects || dashboardData.activeProjects.length})
                                    </Button>
                                </Box>
                            )}
                    </Box>

                </Grid>

                {/* Right Column - Sidebar */}
                <Grid item xs={12} lg={4}>


                    {/* Support Section */}
                    <Card elevation={2}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                                Need Help?
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Our team is here to support you 24/7
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<Message />}
                                    fullWidth
                                    onClick={() => window.dispatchEvent(new CustomEvent('eqaya:open-ai-chat'))}
                                    sx={{ justifyContent: 'flex-start', textTransform: 'none', borderColor: brand.primary, color: brand.primary, '&:hover': { backgroundColor: brand.primary, color: 'white' } }}
                                >
                                    Live Chat Support
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<Info />}
                                    fullWidth
                                    onClick={() => navigate('/faq')}
                                    sx={{ justifyContent: 'flex-start', textTransform: 'none', borderColor: brand.primary, color: brand.primary, '&:hover': { backgroundColor: brand.primary, color: 'white' } }}
                                >
                                    FAQ
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Activity Feed - Full Width */}
            <Box sx={{ mt: 3 }}>
                <ActivityFeed activities={dashboardData.recentActivity} />
            </Box>

            {/* Pending Reviews */}
            <Box sx={{ mt: 3 }}>
                <PendingReviews userId={user?.id} />
            </Box>

            {/* Saved homes — hides itself when the buyer has saved none */}
            <SavedHomesCard />

            {/* Schedule Site Visit Dialog */}
            <Dialog
                open={showScheduleDialog}
                onClose={() => setShowScheduleDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogCloseButton onClick={() => setShowScheduleDialog(false)} />
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <VideoCall sx={{ color: brand.primary }} />
                        Schedule Site Visit
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Schedule a virtual or in-person site visit to monitor your project progress
                                </Typography>
                            </Grid>
                            
                            <Grid item xs={12}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Project"
                                    value={scheduleForm.projectId}
                                    onChange={(e) => setScheduleForm({ ...scheduleForm, projectId: e.target.value })}
                                    required
                                >
                                    {dashboardData.activeProjects.length === 0 ? (
                                        <MenuItem value="">No active projects</MenuItem>
                                    ) : (
                                        dashboardData.activeProjects.map(project => (
                                            <MenuItem key={project.id} value={project.id}>
                                                {project.name}
                                            </MenuItem>
                                        ))
                                    )}
                                </TextField>
                            </Grid>
                            
                            <Grid item xs={12}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Visit Type"
                                    value={scheduleForm.visitType}
                                    onChange={(e) => setScheduleForm({ ...scheduleForm, visitType: e.target.value })}
                                    required
                                >
                                    <MenuItem value="video_call">Video Call</MenuItem>
                                    <MenuItem value="drone_flyover">Drone Flyover</MenuItem>
                                    <MenuItem value="in_person">In-Person Visit</MenuItem>
                                    <MenuItem value="photo_update">Photo Update</MenuItem>
                                </TextField>
                            </Grid>
                            
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    type="datetime-local"
                                    label="Date & Time"
                                    value={scheduleForm.scheduledDate}
                                    onChange={(e) => setScheduleForm({ ...scheduleForm, scheduledDate: e.target.value })}
                                    InputLabelProps={{ shrink: true }}
                                    helperText="Zimbabwe (Harare / CAT) time"
                                    required
                                />
                            </Grid>
                            
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label="Notes (Optional)"
                                    value={scheduleForm.notes}
                                    onChange={(e) => setScheduleForm({ ...scheduleForm, notes: e.target.value })}
                                    placeholder="Any specific areas you want to focus on?"
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowScheduleDialog(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleScheduleSiteVisit}
                        disabled={!scheduleForm.projectId || !scheduleForm.scheduledDate}
                        sx={{ bgcolor: brand.primary, '&:hover': { bgcolor: brand.primaryDark } }}
                    >
                        Schedule Visit
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for feedback messages */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* CSS for rotating animation */}
            <style>{`
                @keyframes rotate {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                .rotating {
                    animation: rotate 1s linear infinite;
                }
            `}</style>
        </Box>
    );
};

export default EnhancedClientDashboard;
